import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Send, User, ChevronLeft, MessageCircle, AlertCircle, CheckCircle } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

const apiUrl = process.env.REACT_APP_API_URL;

export default function MessagesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const chatParam = searchParams.get("chat");
  
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [activeChat, setActiveChat] = useState(null);
  const [user, setUser] = useState(null);
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const uStr = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (uStr) {
      setUser(JSON.parse(uStr));
      fetchConversations();
    }
  }, []);

  useEffect(() => {
    if (chatParam) {
      fetchMessages(chatParam);
      const conv = conversations.find(c => c.id == chatParam);
      if (conv) setActiveChat(conv);
    } else {
      setActiveChat(null);
      setMessages([]);
    }
  }, [chatParam, conversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchConversations = async () => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await fetch(`${apiUrl}/api/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setConversations(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const fetchMessages = async (id) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await fetch(`${apiUrl}/api/messages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setMessages(data);
      
      // Mark as read
      fetch(`${apiUrl}/api/messages/read/${id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !chatParam) return;

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      const res = await fetch(`${apiUrl}/api/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ conversationId: chatParam, content: newMessage })
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessages([...messages, data]);
        setNewMessage("");
        fetchConversations(); // Refresh last message in list
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-7xl w-full mx-auto p-4 md:p-6 grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-140px)]">
        
        {/* CONVERSATION LIST */}
        <div className={`bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col ${activeChat ? 'hidden lg:flex' : 'flex'}`}>
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900">Messages</h2>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-gray-500 italic">Loading chats...</div>
            ) : conversations.length === 0 ? (
              <div className="p-10 text-center space-y-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                  <MessageCircle className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500">No conversations yet.</p>
              </div>
            ) : (
              conversations.map((conv) => {
                const isBuyer = conv.buyer_id === user?.id;
                const contactName = isBuyer ? conv.sellerName : conv.buyerName;
                const contactPic = isBuyer ? conv.sellerPicture : conv.buyerPicture;
                
                return (
                  <button
                    key={conv.id}
                    onClick={() => setSearchParams({ chat: conv.id })}
                    className={`w-full p-4 flex items-center gap-4 transition-colors border-b border-gray-50 hover:bg-gray-50 ${chatParam == conv.id ? 'bg-green-50/50' : ''}`}
                  >
                    <div className="relative">
                      <img 
                        src={conv.postImage || "https://via.placeholder.com/150?text=No+Image"} 
                        className="w-12 h-12 rounded-xl object-cover border bg-gray-50"
                        alt={contactName}
                      />
                      <img 
                        src={contactPic || `https://ui-avatars.com/api/?name=${contactName}`} 
                        className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-white object-cover shadow-sm"
                        alt={contactName}
                      />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex justify-between items-baseline mb-0.5">
                        <h4 className="font-bold text-gray-900 truncate">{contactName}</h4>
                        <span className="text-[10px] text-gray-400">
                          {conv.lastMessageDate ? new Date(conv.lastMessageDate).toLocaleDateString() : ''}
                        </span>
                        {conv.unreadCount > 0 && (
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        )}
                      </div>
                      <p className="text-xs text-green-600 font-medium truncate mb-1">Re: {conv.postTitle}</p>
                      <p className="text-sm text-gray-500 truncate">{conv.lastMessage || 'Start a conversation'}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* CHAT WINDOW */}
        <div className={`lg:col-span-2 bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col ${activeChat ? 'flex' : 'hidden lg:flex'}`}>
          {activeChat ? (
            <>
              {/* CHAT HEADER */}
              <div className="p-4 border-b flex items-center gap-4">
                <button onClick={() => setSearchParams({})} className="lg:hidden p-2 hover:bg-gray-100 rounded-full">
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <img 
                  src={(activeChat.buyer_id === user?.id ? activeChat.sellerPicture : activeChat.buyerPicture) || `https://ui-avatars.com/api/?name=${activeChat.buyer_id === user?.id ? activeChat.sellerName : activeChat.buyerName}`} 
                  className="w-10 h-10 rounded-full object-cover border"
                />
                <div>
                  <h3 className="font-bold text-gray-900">
                    {activeChat.buyer_id === user?.id ? activeChat.sellerName : activeChat.buyerName}
                  </h3>
                  <p className="text-[11px] text-green-600 font-bold uppercase tracking-wider">
                    Item: {activeChat.postTitle}
                  </p>
                </div>
              </div>

              {/* ITEM INFO BAR */}
              <div className="px-6 py-3 bg-gray-50 border-b flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 overflow-hidden">
                  <img 
                    src={activeChat.postImage || "https://via.placeholder.com/150?text=No+Image"} 
                    className="w-12 h-12 rounded-lg object-cover border bg-white"
                    alt={activeChat.postTitle}
                  />
                  <div className="truncate">
                    <h4 className="text-sm font-bold text-gray-900 truncate">{activeChat.postTitle}</h4>
                    <p className="text-[10px] text-gray-500 uppercase font-semibold">View Item Details</p>
                  </div>
                </div>
                <button 
                  onClick={() => navigate(`/detail/${activeChat.post_id}`)}
                  className="px-4 py-2 bg-white border border-gray-200 text-gray-700 text-xs font-bold rounded-xl hover:bg-gray-100 transition whitespace-nowrap shadow-sm"
                >
                  View Post
                </button>
              </div>

              {/* MESSAGES AREA */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/30">
                {messages.map((msg, i) => {
                  const bubbleStyle = msg.sender_id === user?.id 
                    ? 'bg-green-600 text-white rounded-tr-none' 
                    : (msg.type === 'error' 
                        ? 'bg-red-50 text-red-700 border-red-100 rounded-tl-none' 
                        : msg.type === 'success' 
                          ? 'bg-green-50 text-green-700 border-green-100 rounded-tl-none' 
                          : 'bg-white text-gray-900 border border-gray-100 rounded-tl-none');
                  
                  return (
                    <div 
                      key={msg.id}
                      className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] p-4 rounded-2xl shadow-sm border ${bubbleStyle}`}>
                        <div className="flex items-start gap-2">
                          {msg.sender_id !== user?.id && msg.type === 'error' && <AlertCircle size={14} className="mt-0.5 shrink-0" />}
                          {msg.sender_id !== user?.id && msg.type === 'success' && <CheckCircle size={14} className="mt-0.5 shrink-0" />}
                          <p className="text-sm leading-relaxed">{msg.content}</p>
                        </div>
                        <span className={`text-[10px] mt-1 block opacity-60`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* INPUT AREA */}
              <form onSubmit={handleSendMessage} className="p-4 border-t bg-white flex gap-3">
                <input 
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-3 rounded-xl bg-gray-100 border-transparent focus:bg-white focus:border-green-500 focus:ring-0 transition-all text-sm outline-none"
                />
                <button 
                  type="submit"
                  disabled={!newMessage.trim()}
                  className="w-12 h-12 bg-green-600 text-white rounded-xl flex items-center justify-center hover:bg-green-700 transition disabled:opacity-50 shadow-lg shadow-green-900/20"
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-10 space-y-4">
              <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center">
                <MessageCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Your Conversations</h3>
              <p className="text-gray-500 max-w-xs">Select a chat from the list to view the message history and start talking.</p>
            </div>
          )}
        </div>

      </main>
      
      <div className="hidden lg:block">
         <Footer />
      </div>
    </div>
  );
}
